import { useEffect, useState } from 'react'

const MEMBER_CONTEXT_URL = '/umbraco/surface/membercontext/current'

/**
 * Returns the authenticated member's coarse session state derived from the
 * Umbraco member-context surface endpoint.
 *
 * - `isAuthenticated`: true when the endpoint returns 200 (member is signed in).
 * - `needsRenewal`: true when the cached D365 status + role permit access to
 *   the renewal form. Mirrors the gating used by RenewalBadge / RenewButton.
 * - `role` / `memberCategory` / `feeType`: cached D365 classification used to
 *   gate member-portal sections (Industry Supporters see My Profile
 *   only; membership/organisation sections are Main/Administrator/Finance only).
 * - `canAccessPortalSections`: server-computed role allowlist verdict for the
 *   privileged member-portal sections (Membership Details / Organisation
 *   Details / Organisation Information). FAIL CLOSED: only an explicit `true`
 *   from the server grants access — a 401, a network error or an older API
 *   payload without the field all resolve to denied.
 * - `portalUrls`: member-portal page URLs from CrmMember appsettings
 *   ({ myProfile, membershipDetails, organisationDetails, organisationInformation }),
 *   so components never hardcode them. Null until the session resolves.
 * - `loaded`: true once the endpoint has resolved (success or failure) so
 *   consumers can defer role/category-driven rendering until the data arrives.
 *
 * The fetch is fire-and-forget on mount. A 401 (or any network error) leaves
 * the flags false, which matches the signed-out experience.
 */
export function useMemberSession() {
  // Read and immediately clear the one-time hint written by RenewalsEmbedForm
  // before it navigates away. The Umbraco member cache is only refreshed on
  // login, so this bridges the gap between form submission and the next login.
  // The lazy initialiser runs synchronously so the hint is consumed by the
  // first useMemberSession caller and won't be read twice.
  const [statusHint] = useState(() => {
    const hint = sessionStorage.getItem('nds_member_status_hint')
    if (hint) sessionStorage.removeItem('nds_member_status_hint')
    return hint ?? null
  })

  const [state, setState] = useState({
    isAuthenticated: false,
    needsRenewal: false,
    status: null,
    invoiceNumber: null,
    role: null,
    memberCategory: null,
    feeType: null,
    canAccessPortalSections: false,
    portalUrls: null,
    loaded: false,
  })

  useEffect(() => {
    let active = true
    fetch(MEMBER_CONTEXT_URL, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active) return
        if (!data) {
          setState((prev) => ({ ...prev, loaded: true }))
          return
        }
        setState({
          isAuthenticated: true,
          needsRenewal: Boolean(data.canAccessRenewalForm),
          // Prefer the fresh hint over the stale cached status when available.
          status: statusHint ?? data.status ?? null,
          invoiceNumber: data.invoiceNumber ?? null,
          role: data.role ?? null,
          memberCategory: data.memberCategory ?? null,
          feeType: data.currentMembershipFeeType ?? null,
          canAccessPortalSections: data.canAccessMemberPortalSections === true,
          portalUrls: data.memberPortalUrls ?? null,
          loaded: true,
        })
      })
      .catch(() => {
        // Network error — leave session in the signed-out state.
        if (active) setState((prev) => ({ ...prev, loaded: true }))
      })
    return () => {
      active = false
    }
  }, [])

  return state
}

/**
 * Reads the ASP.NET Core antiforgery token rendered into the page by
 * `_Layout.cshtml`. Required when POSTing to surface controllers decorated
 * with `[ValidateAntiForgeryToken]` (e.g. /member/logout).
 */
export function getCsrfToken() {
  return document.querySelector('meta[name="request-verification-token"]')?.content ?? ''
}

/**
 * Current page path (+ query) for use as a logout `returnUrl`, so the member
 * lands back on the page they logged out from instead of the site root. The
 * server validates this with `Url.IsLocalUrl` before redirecting. Returns an
 * empty string when there's no window (SSR), in which case the server falls
 * back to its configured LoggedOutUrl.
 */
export function getReturnUrl() {
  if (typeof window === 'undefined') return ''
  const path = window.location.pathname.replace(/\/$/, '') || '/'
  // Member-area pages (e.g. the /member profile) require authentication, so
  // returning the member there after logout just bounces them to /member/login.
  // Send no returnUrl for those pages so the server falls back to its configured
  // LoggedOutUrl (the homepage). On other (locked-content) pages we keep the
  // returnUrl so the signed-out content banner re-renders in place.
  if (path === '/member' || path.startsWith('/member/')) return ''
  return window.location.pathname + window.location.search
}
